import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useApiClient } from "@/api/client"
import { useWorkspace } from "@/workspaces/WorkspaceContext"

export type AttributeOption = {
  label: string
  value: string
  color?: string
}

export type AttributeAIAutofill = {
  enabled: boolean
  type?: "web_research_agent" | "prompt_completion" | "summarize_record" | "classify_record"
  prompt?: string
  output_type?: string
}

export type AttributeDefinition = {
  id: string
  object_type_id: string
  workspace_id?: string
  slug: string
  name: string
  description?: string
  attribute_type: string
  is_system: boolean
  is_required: boolean
  display_order: number
  config: {
    description?: string
    options?: AttributeOption[]
    target_object_type?: string
    ai_autofill?: AttributeAIAutofill
    [key: string]: unknown
  }
}

export type AttributeTypeCatalogItem = {
  id: string
  workspace_id?: string
  key: string
  label: string
  description: string
  storage_type: string
  supports_multiple: boolean
  supports_options: boolean
  supports_references: boolean
  supports_ai_autofill: boolean
  default_config?: Record<string, unknown>
  is_system: boolean
}

export type CreateAttributeDefinitionInput = {
  name: string
  slug?: string
  description?: string
  attribute_type: string
  is_required?: boolean
  config?: Record<string, unknown>
}

export type AttributeLayoutItem = {
  attribute_id: string
  section: string
  order: number
}

export type AttributeLayoutPreference = {
  id: string
  workspace_id: string
  object_type_id: string
  surface: string
  scope: "workspace" | "personal"
  user_id?: string | null
  items: AttributeLayoutItem[]
  updated_at: string
}

export type AttributeLayoutState = {
  surface: string
  workspace?: AttributeLayoutPreference | null
  personal?: AttributeLayoutPreference | null
}

export function useAttributeDefinitions(objectTypeSlug: string) {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const slug = workspace.slug

  return useQuery({
    queryKey: ["attribute-definitions", slug, objectTypeSlug],
    queryFn: async () => {
      const result = await fetchWithAuth<{ items: AttributeDefinition[] }>(
        `/api/v1/workspaces/${slug}/object-types/${objectTypeSlug}/attributes`,
      )
      return result.items
    },
    enabled: Boolean(slug && objectTypeSlug),
    staleTime: 30_000,
  })
}

export function useAttributeTypeCatalog() {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const slug = workspace.slug

  return useQuery({
    queryKey: ["attribute-type-catalog", slug],
    queryFn: async () => {
      const result = await fetchWithAuth<{ items: AttributeTypeCatalogItem[] }>(
        `/api/v1/workspaces/${slug}/attribute-types`,
      )
      return result.items
    },
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  })
}

export function useCreateAttributeDefinition(objectTypeSlug: string) {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const qc = useQueryClient()
  const slug = workspace.slug

  return useMutation({
    mutationFn: (input: CreateAttributeDefinitionInput) =>
      fetchWithAuth<AttributeDefinition>(
        `/api/v1/workspaces/${slug}/object-types/${objectTypeSlug}/attributes`,
        {
          method: "POST",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attribute-definitions", slug, objectTypeSlug] })
    },
  })
}

export function useDeleteAttributeDefinition(objectTypeSlug: string) {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const qc = useQueryClient()
  const slug = workspace.slug

  return useMutation({
    mutationFn: (attributeId: string) =>
      fetchWithAuth<{ status: string }>(
        `/api/v1/workspaces/${slug}/object-types/${objectTypeSlug}/attributes/${attributeId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attribute-definitions", slug, objectTypeSlug] })
    },
  })
}

export function useAttributeLayout(objectTypeSlug: string, surface: string) {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const slug = workspace.slug

  return useQuery({
    queryKey: ["attribute-layout", slug, objectTypeSlug, surface],
    queryFn: () =>
      fetchWithAuth<AttributeLayoutState>(
        `/api/v1/workspaces/${slug}/object-types/${objectTypeSlug}/attribute-layouts/${surface}`,
      ),
    enabled: Boolean(slug && objectTypeSlug && surface),
    staleTime: 10_000,
  })
}

export function useSaveAttributeLayout(objectTypeSlug: string, surface: string) {
  const { fetchWithAuth } = useApiClient()
  const { workspace } = useWorkspace()
  const qc = useQueryClient()
  const slug = workspace.slug

  return useMutation({
    mutationFn: (input: { scope: "workspace" | "personal"; items: AttributeLayoutItem[] }) =>
      fetchWithAuth<AttributeLayoutPreference>(
        `/api/v1/workspaces/${slug}/object-types/${objectTypeSlug}/attribute-layouts/${surface}`,
        {
          method: "PUT",
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attribute-layout", slug, objectTypeSlug, surface] })
      qc.invalidateQueries({ queryKey: ["attribute-definitions", slug, objectTypeSlug] })
    },
  })
}
